// Cookie-consent record (Garante "Linee guida cookie" 10 June 2021 compliant
// banner relies on this). Stored client-side only, in localStorage, never
// sent to our backend or Firestore. All storage access wrapped in try/catch —
// private mode / disabled storage degrades to "no decision yet" rather than
// throwing.
//
// Shape: { v: 1, date: ISO string, necessary: true, analytics: boolean }
// `necessary` is always true (technical storage needs no consent, art. 122
// comma 1 Codice Privacy) and kept in the record only for transparency in
// devtools / support requests.
//
// Consent expires 12 months after `date` — after that hasDecided() reports
// false again so the banner re-asks, per Garante guidance on consent renewal.
// A stored record whose `v` doesn't match CURRENT_VERSION is also treated as
// "no decision" and re-asked — bumping CURRENT_VERSION is how a future
// schema/category change (e.g. a new consent category) forces re-consent.

const STORAGE_KEY = 'sg.consent';
const CURRENT_VERSION = 1;
const EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 12 months (approx, 365 days)

const listeners = new Set();

function safeLocal() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

function isExpired(record) {
  if (!record || !record.date) return true;
  const ts = Date.parse(record.date);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > EXPIRY_MS;
}

// Returns the stored consent record, or null if none / malformed / expired.
export function getConsent() {
  try {
    const store = safeLocal();
    const raw = store && store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record || typeof record !== 'object') return null;
    if (record.v !== CURRENT_VERSION) return null; // schema changed — re-ask
    if (typeof record.analytics !== 'boolean') return null;
    if (isExpired(record)) return null;
    return {
      v: record.v,
      date: record.date,
      necessary: true,
      analytics: record.analytics,
    };
  } catch (e) {
    return null;
  }
}

// `true` once a non-expired choice is on record.
export function hasDecided() {
  return getConsent() !== null;
}

// Persist a new choice and notify subscribers. `{ analytics: boolean }`.
export function setConsent({ analytics }) {
  const record = {
    v: CURRENT_VERSION,
    date: new Date().toISOString(),
    necessary: true,
    analytics: !!analytics,
  };
  try {
    const store = safeLocal();
    store && store.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    /* storage unavailable — choice just won't persist across reloads */
  }
  notify(record);
  return record;
}

// Subscribe to consent changes (fires on every setConsent call). Returns an
// unsubscribe function.
export function subscribeConsent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(record) {
  listeners.forEach((fn) => {
    try {
      fn(record);
    } catch (e) {
      /* a subscriber's own error must not break the others */
    }
  });
}
